/**
 * @name FileList.js
 * @author 김권영
 * @since 2025. 01. 06.
 * @description
 * 공통 파일 목록 및 다운로드 삭제 기능
 * 
 * @param {string} atchFileId - 파일ID (파일PK번호)
 * @param {function} onFileDeleteMark - 파일 삭제 시 삭제 할 파일 순번 (폼 저장 방식때 사용 - 따로 구현 필요 (ReferenceForm.jsx에 markDelete() 함수 ))
 * @param {boolean} showDelete - 삭제 버튼 활성화 여부 (기본 값 ture)
 * @param {className} className - 파일 <ul className=""> 지정
 * @param {array} deletedFileSns - 기존 파일 중 최종 삭제 예정 파일 순번 fileSn 배열
 * @param {int} onLoaded - 파일 개수 상태
 */
import React, {useEffect}  from 'react';
import { useQuery } from '@tanstack/react-query';
import { get  } from '@utils/axios_api';
import './FileList.css';

const fetchFileDetails = async (atchFileId) => {
  const response = await get('/file/getFileDetails', {
    params: { atchFileId },
  });
  return response.result;
};

const FileList = ({ atchFileId, onFileDeleteMark, showDelete = true, className, deletedFileSns = [], onLoaded }) => {
  const { data: existingFiles = [], isLoading, isError, error} = useQuery(
    ['fileDetails', atchFileId],
    () => fetchFileDetails(atchFileId),
    { enabled: !!atchFileId,
      staleTime: 0,
      refetchOnMount: true,
      refetchOnWindowFocus: false,
     } 
  );


  const handleDownload = async (file) => {
    try {
      const blob = await get('/file/download', {
        params: {
          atchFileId: file.atchFileId,
          fileSn: file.fileSn,
        },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.orignlFileNm);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('파일 다운로드 중 오류 발생:', error);
      alert('파일 다운로드 중 오류가 발생했습니다.');
    }
  };

  const handleDeleteClick  = async (file) => {
    if (window.confirm(`${file.orignlFileNm} 파일을 삭제하시겠습니까?`)) {
      onFileDeleteMark?.(file);
    }
  };

  const displayedFiles = existingFiles.filter(
    (file) => !deletedFileSns.includes(file.fileSn)
  );

  useEffect(() => {
  // 파일 목록 또는 삭제표시가 바뀔 때마다 실행
    onLoaded?.(displayedFiles.length);
  }, [displayedFiles.length, onLoaded]);

  if (!atchFileId) return null;
  if (isLoading) return <div>파일 목록을 불러오는 중입니다...</div>;
  if (isError) return <div>파일 목록을 불러오는 중 오류가 발생했습니다: {error.message}</div>;
  if (existingFiles.length === 0) return null;
  
  // 표시할 파일이 0개라면 null 반환
  if (displayedFiles.length === 0) return null;



  return (
    <ul className={`existing-files ${className}`}>
      {displayedFiles.map((file) => (
        <li key={file.fileSn}>
           <div className="file-item-container">
              <button type="button" onClick={() => handleDownload(file)} className="file-download-button" >{file.orignlFileNm}</button>
              {showDelete && (
                <button type="button" onClick={() => handleDeleteClick(file)} className="file-delete-button">X</button>
              )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default FileList;
